import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import type { PricedProduct } from '../../../../../services/practiceProductPricing';
import { TREATMENT_CATEGORY } from '../../../../../services/practiceProductPricing';
import {
    currentEffectiveCents,
    feeLineFromProduct,
    isOverriddenAmount,
    isStaleVersion,
    resetFeeLine
} from './feeVersion';
import { AddonRow, CaseProductRow, DraftAmountInput, formatCurrency } from './FeeRows';
import type { DxReportFeeLine, DxReportFees } from '../types';

interface FeesSectionProps {
    /** Full list of billable products (same for every practice). */
    pricedProducts: PricedProduct[];
    /** True when products are still loading or failed to load. */
    productsLoading?: boolean;
    productsError?: string | null;
    value: DxReportFees;
    onChange: (next: DxReportFees) => void;
    readOnly?: boolean;
    accountType?: string | null;
}

const PATIENT_INVESTMENT_MULTIPLIER = 3.5;
const PATIENT_INVESTMENT_MINIMUM_CENTS = 6500;

// Both baseCents and each addon's contribution must be LIST price (not the
// billed amount), so a practice discount never lowers the patient-facing figure.
function recommendedFromCost(baseCents: number, addonLines: DxReportFeeLine[]): number {
    const addonsTotal = addonLines.reduce((s, l) => s + (l.listPriceCents ?? l.amountCents), 0);
    return Math.max(
        PATIENT_INVESTMENT_MINIMUM_CENTS,
        addonsTotal + Math.round(baseCents * PATIENT_INVESTMENT_MULTIPLIER)
    );
}

export function FeesSection({
    pricedProducts,
    productsLoading,
    productsError,
    value,
    onChange,
    readOnly,
    accountType
}: FeesSectionProps) {
    const [casePickerOpen, setCasePickerOpen] = useState(false);
    const [addonPickerOpen, setAddonPickerOpen] = useState(false);
    const [caseSearch, setCaseSearch] = useState('');
    const [addonSearch, setAddonSearch] = useState('');
    const casePickerRef = useRef<HTMLDivElement | null>(null);
    const addonPickerRef = useRef<HTMLDivElement | null>(null);

    // Treatment-category products are the case-product picker's source. Add-ons
    // draw from the WHOLE catalogue, treatment included: a case can legitimately
    // carry a second course of aligners, which bills as its own Tx object
    // alongside the case product. Restricting add-ons to non-treatment products
    // made that unbillable.
    const caseProducts = useMemo(
        () => pricedProducts.filter((p) => p.category === TREATMENT_CATEGORY),
        [pricedProducts]
    );
    const addonProducts = pricedProducts;

    // Fast lookup of current default price by productId - used to compute
    // "practice price" hints and to power the reset-to-default button.
    const productById = useMemo(() => {
        const m = new Map<string, PricedProduct>();
        for (const p of pricedProducts) m.set(p.id, p);
        return m;
    }, [pricedProducts]);

    // Outside-click closes for both pickers.
    useEffect(() => {
        if (!casePickerOpen && !addonPickerOpen) return;
        const handler = (e: MouseEvent) => {
            if (
                casePickerOpen &&
                casePickerRef.current &&
                !casePickerRef.current.contains(e.target as Node)
            ) {
                setCasePickerOpen(false);
            }
            if (
                addonPickerOpen &&
                addonPickerRef.current &&
                !addonPickerRef.current.contains(e.target as Node)
            ) {
                setAddonPickerOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [casePickerOpen, addonPickerOpen]);

    const baseCents = value.caseProduct?.amountCents ?? 0;
    const totalsAddonCents = value.addons.reduce((s, l) => s + l.amountCents, 0);
    const odoTotalCents = baseCents + totalsAddonCents;
    // Drives the patient-investment multiplier - list price, not the billed
    // (possibly discounted) amount above.
    const recommendationBaseCents = value.caseProduct?.listPriceCents ?? baseCents;

    // ---- Case product handlers ------------------------------------------------

    const setCaseLine = useCallback(
        (line: DxReportFeeLine) =>
            onChange({
                ...value,
                caseProduct: line,
                recommendedPatientInvestmentCents: recommendedFromCost(
                    line.listPriceCents ?? line.amountCents,
                    value.addons
                )
            }),
        [onChange, value]
    );

    const selectCaseProduct = useCallback(
        (product: PricedProduct) => {
            setCaseLine(feeLineFromProduct(product));
            setCasePickerOpen(false);
            setCaseSearch('');
        },
        [setCaseLine]
    );

    const changeCaseVersion = useCallback(
        (versionId: string) => {
            const product = value.caseProduct && productById.get(value.caseProduct.productId);
            if (product) setCaseLine(feeLineFromProduct(product, versionId));
        },
        [setCaseLine, value.caseProduct, productById]
    );

    const clearCaseProduct = useCallback(() => {
        onChange({
            ...value,
            caseProduct: null,
            recommendedPatientInvestmentCents: recommendedFromCost(0, value.addons)
        });
    }, [onChange, value]);

    const setCaseProductAmount = useCallback(
        (cents: number) => {
            if (!value.caseProduct) return;
            const product = productById.get(value.caseProduct.productId);
            const isOverridden = product
                ? isOverriddenAmount(product, value.caseProduct.versionId, cents)
                : true;
            const nextLine: DxReportFeeLine = {
                ...value.caseProduct,
                amountCents: cents,
                isOverridden
            };
            onChange({
                ...value,
                caseProduct: nextLine,
                recommendedPatientInvestmentCents: recommendedFromCost(
                    nextLine.listPriceCents ?? cents,
                    value.addons
                )
            });
        },
        [onChange, value, productById]
    );

    const resetCaseProductPrice = useCallback(() => {
        if (!value.caseProduct) return;
        const product = productById.get(value.caseProduct.productId);
        if (!product) return;
        setCaseLine(resetFeeLine(product, value.caseProduct));
    }, [setCaseLine, value.caseProduct, productById]);

    // ---- Addon handlers --------------------------------------------------------

    const setAddonLines = useCallback(
        (nextAddons: DxReportFeeLine[]) =>
            onChange({
                ...value,
                addons: nextAddons,
                recommendedPatientInvestmentCents: recommendedFromCost(
                    recommendationBaseCents,
                    nextAddons
                )
            }),
        [onChange, value, recommendationBaseCents]
    );

    const addAddonProduct = useCallback(
        (product: PricedProduct) => {
            if (value.addons.some((a) => a.productId === product.id)) return;
            setAddonLines([...value.addons, feeLineFromProduct(product)]);
            setAddonPickerOpen(false);
            setAddonSearch('');
        },
        [setAddonLines, value.addons]
    );

    const removeAddon = useCallback(
        (productId: string) => setAddonLines(value.addons.filter((a) => a.productId !== productId)),
        [setAddonLines, value.addons]
    );

    const setAddonAmount = useCallback(
        (productId: string, cents: number) => {
            const product = productById.get(productId);
            setAddonLines(
                value.addons.map((a) => {
                    if (a.productId !== productId) return a;
                    const isOverridden = product
                        ? isOverriddenAmount(product, a.versionId, cents)
                        : true;
                    return { ...a, amountCents: cents, isOverridden };
                })
            );
        },
        [setAddonLines, value.addons, productById]
    );

    const resetAddonPrice = useCallback(
        (addon: DxReportFeeLine) => {
            const product = productById.get(addon.productId);
            if (!product) return;
            setAddonLines(
                value.addons.map((a) =>
                    a.productId === addon.productId ? resetFeeLine(product, a) : a
                )
            );
        },
        [setAddonLines, value.addons, productById]
    );

    const changeAddonVersion = useCallback(
        (productId: string, versionId: string) => {
            const product = productById.get(productId);
            if (!product) return;
            setAddonLines(
                value.addons.map((a) =>
                    a.productId === productId ? feeLineFromProduct(product, versionId) : a
                )
            );
        },
        [setAddonLines, value.addons, productById]
    );

    // ---- Misc fields -----------------------------------------------------------

    const handleRecommendedChange = useCallback(
        (cents: number) => onChange({ ...value, recommendedPatientInvestmentCents: cents }),
        [onChange, value]
    );

    const handleNotesChange = useCallback(
        (notes: string) => onChange({ ...value, notes }),
        [onChange, value]
    );

    // ---- Picker filtering ------------------------------------------------------

    const filteredCaseProducts = useMemo(() => {
        const q = caseSearch.trim().toLowerCase();
        return q ? caseProducts.filter((p) => p.name.toLowerCase().includes(q)) : caseProducts;
    }, [caseProducts, caseSearch]);

    const usedAddonIds = useMemo(
        () => new Set(value.addons.map((a) => a.productId)),
        [value.addons]
    );
    const filteredAddonProducts = useMemo(() => {
        const q = addonSearch.trim().toLowerCase();
        const available = addonProducts.filter((p) => !usedAddonIds.has(p.id));
        return q ? available.filter((p) => p.name.toLowerCase().includes(q)) : available;
    }, [addonProducts, usedAddonIds, addonSearch]);

    const caseProductForReset = value.caseProduct
        ? (productById.get(value.caseProduct.productId) ?? null)
        : null;
    const caseResetTargetCents = caseProductForReset
        ? currentEffectiveCents(caseProductForReset, value.caseProduct?.versionId)
        : null;

    return (
        <div className="flex flex-col gap-4">
            {productsError && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    {productsError}
                </div>
            )}

            <Field label="Practice account type">
                {accountType ? (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                        {accountType}
                    </span>
                ) : (
                    <span className="text-xs text-gray-400 italic">Not set</span>
                )}
            </Field>

            {/* ---- Case product ----------------------------------------------- */}
            <Field
                label="Case product"
                hint="The treatment product billed for this case. Price defaults to your practice catalog; override per-case if needed."
            >
                {value.caseProduct ? (
                    <CaseProductRow
                        line={value.caseProduct}
                        resetTargetCents={caseResetTargetCents}
                        versions={caseProductForReset?.matchingVersions ?? []}
                        resolvedVersionId={caseProductForReset?.resolvedVersionId ?? null}
                        isStale={
                            caseProductForReset != null &&
                            isStaleVersion(caseProductForReset, value.caseProduct.versionId)
                        }
                        readOnly={readOnly}
                        onVersionChange={changeCaseVersion}
                        onAmountChange={setCaseProductAmount}
                        onReset={resetCaseProductPrice}
                        onClear={clearCaseProduct}
                    />
                ) : (
                    <ProductPicker
                        label={productsLoading ? 'Loading products…' : 'Select case product'}
                        disabled={readOnly || productsLoading}
                        open={casePickerOpen}
                        onOpenChange={setCasePickerOpen}
                        search={caseSearch}
                        onSearchChange={setCaseSearch}
                        products={filteredCaseProducts}
                        onSelect={selectCaseProduct}
                        pickerRef={casePickerRef}
                        emptyLabel={
                            caseProducts.length === 0
                                ? 'No treatment products configured.'
                                : 'No matches.'
                        }
                    />
                )}
            </Field>

            {/* ---- Addons ----------------------------------------------------- */}
            <Field
                label="Add-ons"
                hint="Optional devices and services. Prices default to your practice catalog when known."
            >
                <div className="flex flex-col gap-2">
                    {value.addons.length === 0 ? (
                        <div className="text-xs text-gray-500 italic px-1">
                            No add-ons selected.
                        </div>
                    ) : (
                        value.addons.map((addon) => {
                            const addonProduct = productById.get(addon.productId) ?? null;
                            return (
                                <AddonRow
                                    key={addon.productId}
                                    line={addon}
                                    resetTargetCents={
                                        addonProduct
                                            ? currentEffectiveCents(addonProduct, addon.versionId)
                                            : null
                                    }
                                    versions={addonProduct?.matchingVersions ?? []}
                                    resolvedVersionId={addonProduct?.resolvedVersionId ?? null}
                                    isStale={
                                        addonProduct != null &&
                                        isStaleVersion(addonProduct, addon.versionId)
                                    }
                                    readOnly={readOnly}
                                    onVersionChange={(versionId) =>
                                        changeAddonVersion(addon.productId, versionId)
                                    }
                                    onAmountChange={(cents) =>
                                        setAddonAmount(addon.productId, cents)
                                    }
                                    onReset={() => resetAddonPrice(addon)}
                                    onRemove={() => removeAddon(addon.productId)}
                                />
                            );
                        })
                    )}

                    <ProductPicker
                        label="Add add-on"
                        disabled={readOnly || productsLoading}
                        open={addonPickerOpen}
                        onOpenChange={setAddonPickerOpen}
                        search={addonSearch}
                        onSearchChange={setAddonSearch}
                        products={filteredAddonProducts}
                        onSelect={addAddonProduct}
                        pickerRef={addonPickerRef}
                        variant="add"
                        emptyLabel={
                            addonProducts.length === 0
                                ? 'No products configured.'
                                : usedAddonIds.size === addonProducts.length
                                  ? 'All products already added.'
                                  : 'No matches.'
                        }
                    />
                </div>
            </Field>

            <Field
                label="Recommended patient investment"
                hint={`Auto-calculated as base × ${PATIENT_INVESTMENT_MULTIPLIER} + add-ons (min $${PATIENT_INVESTMENT_MINIMUM_CENTS / 100}). Override if needed.`}
            >
                <div className="flex items-center gap-1 max-w-xs">
                    <span className="text-sm text-gray-500">$</span>
                    <DraftAmountInput
                        cents={value.recommendedPatientInvestmentCents}
                        readOnly={readOnly}
                        onChange={handleRecommendedChange}
                        className="appearance-none block w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-sm tabular-nums focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500"
                    />
                </div>
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <TotalCard
                    title="ODO total (case product + add-ons)"
                    amountCents={odoTotalCents}
                    breakdown={[
                        ...(value.caseProduct
                            ? [
                                  {
                                      label: value.caseProduct.productName,
                                      amount: value.caseProduct.amountCents
                                  }
                              ]
                            : []),
                        ...value.addons.map((a) => ({
                            label: a.productName,
                            amount: a.amountCents
                        }))
                    ]}
                />
                <TotalCard
                    title="Patient investment"
                    amountCents={value.recommendedPatientInvestmentCents}
                    accent="yellow"
                />
            </div>

            <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Notes (optional)
                </label>
                <textarea
                    value={value.notes ?? ''}
                    disabled={readOnly}
                    onChange={(e) => handleNotesChange(e.target.value)}
                    maxLength={2000}
                    rows={2}
                    placeholder="Caveats or assumptions about the fee structure (e.g. financing terms)."
                    className="appearance-none block w-full px-2.5 py-1.5 border border-gray-300 rounded-md placeholder-gray-400 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 resize-none disabled:bg-gray-50 disabled:text-gray-500"
                />
            </div>
        </div>
    );
}

interface ProductPickerProps {
    label: string;
    disabled?: boolean;
    open: boolean;
    onOpenChange: (next: boolean) => void;
    search: string;
    onSearchChange: (next: string) => void;
    products: PricedProduct[];
    onSelect: (product: PricedProduct) => void;
    pickerRef: React.MutableRefObject<HTMLDivElement | null>;
    variant?: 'select' | 'add';
    emptyLabel: string;
}

function ProductPicker({
    label,
    disabled,
    open,
    onOpenChange,
    search,
    onSearchChange,
    products,
    onSelect,
    pickerRef,
    variant = 'select',
    emptyLabel
}: ProductPickerProps) {
    const triggerClass =
        variant === 'add'
            ? 'flex items-center gap-1.5 px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition-colors w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed'
            : 'flex items-center justify-between gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed';

    return (
        <div className="relative" ref={pickerRef}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => onOpenChange(!open)}
                className={triggerClass}
            >
                {variant === 'add' && <PlusIcon className="w-4 h-4" />}
                <span className={variant === 'add' ? '' : 'truncate'}>{label}</span>
                {variant === 'select' && <span className="text-gray-400 text-xs">▾</span>}
            </button>
            {open && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden flex flex-col max-h-72">
                    <div className="border-b border-gray-100 p-2 flex-shrink-0">
                        <input
                            type="text"
                            autoFocus
                            value={search}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder="Search products…"
                            className="appearance-none block w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                    </div>
                    <div className="overflow-y-auto flex-1">
                        {products.length === 0 ? (
                            <div className="px-3 py-2 text-xs text-gray-500 italic">
                                {emptyLabel}
                            </div>
                        ) : (
                            products.map((product) => {
                                const effective = currentEffectiveCents(product);
                                const hasDiscount =
                                    product.defaultPriceCents != null &&
                                    effective !== product.defaultPriceCents;
                                return (
                                    <button
                                        key={product.id}
                                        type="button"
                                        onClick={() => onSelect(product)}
                                        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-gray-50 transition-colors text-left"
                                    >
                                        <span className="text-gray-800 truncate">
                                            {product.name}
                                        </span>
                                        <span className="flex items-center gap-1.5 tabular-nums text-xs flex-shrink-0">
                                            {hasDiscount && (
                                                <span className="text-gray-400 line-through">
                                                    {formatCurrency(product.defaultPriceCents ?? 0)}
                                                </span>
                                            )}
                                            <span className="text-gray-500">
                                                {effective != null
                                                    ? formatCurrency(effective)
                                                    : '-'}
                                            </span>
                                        </span>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

interface FieldProps {
    label: string;
    hint?: string;
    children: React.ReactNode;
}

function Field({ label, hint, children }: FieldProps) {
    return (
        <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
            {children}
            {hint && <div className="text-[11px] text-gray-500 mt-1">{hint}</div>}
        </div>
    );
}

interface TotalCardProps {
    title: string;
    amountCents: number;
    breakdown?: Array<{ label: string; amount: number }>;
    accent?: 'gray' | 'yellow';
}

function TotalCard({ title, amountCents, breakdown, accent = 'gray' }: TotalCardProps) {
    const accentClasses =
        accent === 'yellow' ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50 border-gray-200';
    return (
        <div className={`rounded-lg border ${accentClasses} p-3`}>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-1">
                {title}
            </div>
            <div className="text-xl font-bold text-gray-900 tabular-nums">
                {formatCurrency(amountCents)}
            </div>
            {breakdown && breakdown.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200 flex flex-col gap-0.5 text-[11px] text-gray-600">
                    {breakdown.map((line, i) => (
                        <div key={`${line.label}-${i}`} className="flex justify-between">
                            <span className="truncate pr-2">{line.label}</span>
                            <span className="tabular-nums">{formatCurrency(line.amount)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
